import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { Quill } from '../patches/PatchedQuill';
export const supportedFormats = [
    'bold',
    'italic',
    'link',
    'strike',
    'underline',
    'header',
    'indent',
    'list',
    'template-variable'
];
export const EMPTY_QUILL_DELTA = {
    type: 'quill-delta',
    value: {
        ops: []
    }
};
export function extractQuillDelta(maybeRichText) {
    try {
        const parsed = JSON.parse(maybeRichText);
        if (parsed.type === 'quill-delta' && 'value' in parsed) {
            return parsed;
        }
        return null;
    } catch (error) {
        return null;
    }
}
function opToPlainText(op) {
    var _insert_templatevariable_label, _insert_templatevariable;
    const insert = op.insert;
    if (typeof insert === 'string') {
        return insert;
    }
    if (insert == null) {
        return '';
    }
    const label = (_insert_templatevariable = insert['template-variable']) === null || _insert_templatevariable === void 0 ? void 0 : (_insert_templatevariable_label = _insert_templatevariable.label) === null || _insert_templatevariable_label === void 0 ? void 0 : _insert_templatevariable_label.trim();
    return label ? "{{".concat(label, "}}") : '';
}
export function convertRichTextToPlainText(richTextString) {
    var _ref;
    var _parsed_value;
    if (!richTextString) {
        return '';
    }
    const parsed = extractQuillDelta(richTextString);
    if (!parsed) {
        return richTextString;
    }
    const ops = (_ref = (_parsed_value = parsed.value) === null || _parsed_value === void 0 ? void 0 : _parsed_value.ops) !== null && _ref !== void 0 ? _ref : [];
    return ops.map(opToPlainText).join('').trimEnd();
}
export function isEmptyRichText(richText) {
    return richText.value.ops.length === 0 || richText.value.ops.length === 1 && richText.value.ops[0].insert.replaceAll('\\n', '').replaceAll('\n', '').trim() === '';
}
export function isEmptyRichTextString(richTextString) {
    if (!richTextString) {
        return true;
    }
    const parsed = extractQuillDelta(richTextString);
    if (!parsed) {
        return true;
    }
    return isEmptyRichText(parsed);
}
export function normalizeDescription(description) {
    return isEmptyRichText(description) ? '' : JSON.stringify(description);
}
export function convertStrToRichText(maybeHTML) {
    const isHTML = maybeHTML.match(/(<([^>]+)>)/gi) != null;
    if (!isHTML) {
        return maybeHTML;
    } else {
        const containerEl = document.createElement('div');
        containerEl.style.height = '0';
        containerEl.style.visibility = 'hidden';
        document.body.appendChild(containerEl);
        const quill = new Quill(containerEl, {
            formats: supportedFormats
        });
        quill.clipboard.dangerouslyPasteHTML(maybeHTML);
        const pastedDelta = quill.getContents();
        document.body.removeChild(containerEl);
        const richTextString = JSON.stringify(makeParsedRichText(pastedDelta));
        return richTextString.replace(/<([^>]+)>/g, '&lt;$1&gt;');
    }
}
function normalizeHTMLWhitespace(html) {
    return html// Remove whitespace between tags only (e.g., </p>   <p> → </p><p>)
    // This preserves spaces before inline tags that follow text (e.g., "The <strong>")
    .replace(/>\s+</g, '><').trim();
}
export function convertHTMLToRichText(html) {
    // Check if input is HTML
    const isHTML = /<\/?[a-z][\s\S]*>/i.test(html);
    if (!isHTML) {
        return makeParsedRichText({
            ops: [
                {
                    insert: html + '\n'
                }
            ]
        });
    }
    // 🔑 Normalize whitespace FIRST
    html = normalizeHTMLWhitespace(html);
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const ops = [];
    function walk(node) {
        let inheritedAttrs = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : {}, parentNode = arguments.length > 2 ? arguments[2] : void 0;
        // ---------- TEXT NODE ----------
        if (node.nodeType === Node.TEXT_NODE) {
            var _node_textContent;
            let text = (_node_textContent = node.textContent) !== null && _node_textContent !== void 0 ? _node_textContent : '';
            // Check if there are element siblings before/after this text node
            // If so, preserve leading/trailing spaces
            const siblings = parentNode ? Array.from(parentNode.childNodes) : [];
            const nodeIndex = siblings.indexOf(node);
            const hasElementBefore = nodeIndex > 0 && siblings[nodeIndex - 1].nodeType === Node.ELEMENT_NODE;
            const hasElementAfter = nodeIndex < siblings.length - 1 && siblings[nodeIndex + 1].nodeType === Node.ELEMENT_NODE;
            // Preserve spaces but collapse multiple consecutive spaces/newlines/tabs into single space
            // This preserves spaces after colons and punctuation while normalizing excessive whitespace
            text = text.replace(/[ \t\n\r]+/g, ' ');
            // If this text node is between elements (has element before and after),
            // or has an element before/after, preserve leading/trailing spaces
            if (hasElementBefore || hasElementAfter) {
                // Preserve the text as-is (spaces already normalized to single space)
                if (text !== '') {
                    ops.push({
                        insert: text,
                        attributes: inheritedAttrs
                    });
                }
            } else {
                // Only trim if not between elements
                if (text.trim() !== '' || text === ' ') {
                    ops.push({
                        insert: text,
                        attributes: inheritedAttrs
                    });
                }
            }
            return;
        }
        // ---------- ELEMENT NODE ----------
        if (node.nodeType !== Node.ELEMENT_NODE) return;
        const el = node;
        const tag = el.tagName;
        const attrs = _object_spread({}, inheritedAttrs);
        // ---------- INLINE FORMATS ----------
        if (tag === 'STRONG' || tag === 'B') attrs.bold = true;
        if (tag === 'I' || tag === 'EM') attrs.italic = true;
        if (tag === 'U') attrs.underline = true;
        if (tag === 'S' || tag === 'STRIKE') attrs.strike = true;
        if (tag === 'A' && el.hasAttribute('href')) {
            attrs.link = el.getAttribute('href');
        }
        // ---------- HEADINGS ----------
        if (tag === 'H1' || tag === 'H2') {
            var _lastOp_attributes;
            const header = tag === 'H1' ? 1 : 2;
            // Add a newline before the heading if there are sibling elements before it
            const siblings = parentNode ? Array.from(parentNode.childNodes) : [];
            const nodeIndex = siblings.indexOf(el);
            const hasSiblingBefore = nodeIndex > 0;
            // Also check if there's already content in ops
            const hasContentBefore = ops.length > 0;
            const lastOp = ops.length > 0 ? ops[ops.length - 1] : null;
            const isLastOpHeader = (lastOp === null || lastOp === void 0 ? void 0 : lastOp.insert) === '\n' && (lastOp === null || lastOp === void 0 ? void 0 : (_lastOp_attributes = lastOp.attributes) === null || _lastOp_attributes === void 0 ? void 0 : _lastOp_attributes.header);
            // Add newline if there's a sibling before or content before (and it's not already a header)
            if ((hasSiblingBefore || hasContentBefore) && !isLastOpHeader) {
                ops.push({
                    insert: '\n'
                });
            }
            el.childNodes.forEach((child)=>walk(child, attrs, el));
            ops.push({
                insert: '\n',
                attributes: {
                    header
                }
            });
            return;
        }
        // ---------- LIST ITEM ----------
        if (tag === 'LI') {
            el.childNodes.forEach((child)=>walk(child, attrs, el));
            const parentList = el.parentElement;
            // Always add the list attribute newline to keep the item in the list
            // The cleanup phase will handle spacing appropriately
            ops.push({
                insert: '\n',
                attributes: {
                    list: (parentList === null || parentList === void 0 ? void 0 : parentList.tagName) === 'OL' ? 'ordered' : 'bullet'
                }
            });
            return;
        }
        // ---------- LIST CONTAINER ----------
        if (tag === 'UL' || tag === 'OL') {
            el.childNodes.forEach((child)=>walk(child, attrs, el));
            return; // ❗️NO newline here
        }
        // ---------- PARAGRAPH / BLOCK ----------
        if (tag === 'P' || tag === 'DIV') {
            // Check if paragraph is empty (no text content)
            const hasTextContent = Array.from(el.childNodes).some((child)=>{
                var _child_textContent;
                return child.nodeType === Node.TEXT_NODE && ((_child_textContent = child.textContent) === null || _child_textContent === void 0 ? void 0 : _child_textContent.trim()) !== '';
            });
            const hasNonEmptyChildren = Array.from(el.childNodes).some((child)=>child.nodeType === Node.ELEMENT_NODE);
            el.childNodes.forEach((child)=>walk(child, attrs, el));
            // If paragraph is empty, add an extra newline to create an empty line
            if (!hasTextContent && !hasNonEmptyChildren) {
                ops.push({
                    insert: '\n'
                });
            }
            ops.push({
                insert: '\n'
            });
            return;
        }
        // ---------- DEFAULT (INLINE ELEMENTS) ----------
        el.childNodes.forEach((child)=>walk(child, attrs, el));
    }
    // Walk document body
    doc.body.childNodes.forEach((node)=>walk(node, {}, doc.body));
    // ---------- CLEANUP ----------
    const cleanedOps = [];
    let newlineCount = 0;
    let followingListNewline = false;
    let allowedNewlineAfterList = false;
    for(let i = 0; i < ops.length; i++){
        var _op_attributes;
        const op = ops[i];
        const isListNewline = op.insert === '\n' && ((_op_attributes = op.attributes) === null || _op_attributes === void 0 ? void 0 : _op_attributes.list);
        if (op.insert === '\n' && !isListNewline) {
            // If this newline follows a list newline, allow exactly 1 newline
            if (followingListNewline && !allowedNewlineAfterList) {
                // Allow the first newline after a list
                cleanedOps.push(op);
                allowedNewlineAfterList = true;
                newlineCount = 0;
            } else if (followingListNewline && allowedNewlineAfterList) {
            // Skip subsequent newlines after we've already allowed 1
            } else {
                newlineCount++;
                // Allow up to 2 consecutive newlines (for empty paragraphs/empty lines)
                if (newlineCount <= 2) {
                    cleanedOps.push(op);
                }
            }
        } else {
            // Track if we're following a list newline
            if (isListNewline) {
                followingListNewline = true;
                allowedNewlineAfterList = false;
            } else if (op.insert !== '\n') {
                // Reset when we see non-newline content (this ends the "following list" state)
                followingListNewline = false;
                allowedNewlineAfterList = false;
            }
            newlineCount = 0;
            cleanedOps.push(op);
        }
    }
    // Ensure final newline (Quill requirement)
    if (!cleanedOps.length || cleanedOps[cleanedOps.length - 1].insert !== '\n') {
        cleanedOps.push({
            insert: '\n'
        });
    }
    return makeParsedRichText({
        ops: cleanedOps
    });
}
export function makeParsedRichText(value) {
    return {
        type: 'quill-delta',
        value
    };
}
export function convertRichTextToHTML(richTextString) {
    let options = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : {};
    if (!richTextString) {
        return '';
    }
    const parsed = extractQuillDelta(richTextString);
    if (!parsed) {
        return '';
    }
    if (isEmptyRichText(parsed)) {
        return '';
    }
    const delta = options.templateVariableMode === 'inline' ? inlineTemplateVariableOpsForRender(parsed.value) : parsed.value;
    const containerEl = document.createElement('div');
    containerEl.style.height = '0';
    containerEl.style.visibility = 'hidden';
    document.body.appendChild(containerEl);
    const quill = new Quill(containerEl, {
        formats: supportedFormats
    });
    quill.setContents(delta);
    const html = quill.getSemanticHTML();
    document.body.removeChild(containerEl);
    return html;
}
function inlineTemplateVariableOpsForRender(delta) {
    var _delta_ops;
    const ops = (_delta_ops = delta.ops) !== null && _delta_ops !== void 0 ? _delta_ops : [];
    const rewritten = ops.map((op)=>{
        if ((op === null || op === void 0 ? void 0 : op.insert) && typeof op.insert === 'object' && 'template-variable' in op.insert) {
            var _tv_label;
            const tv = op.insert['template-variable'];
            const label = tv === null || tv === void 0 ? void 0 : (_tv_label = tv.label) === null || _tv_label === void 0 ? void 0 : _tv_label.trim();
            if (label) {
                return _object_spread_props(_object_spread({}, op), {
                    insert: "{{".concat(label, "}}")
                });
            }
            return _object_spread_props(_object_spread({}, op), {
                insert: ''
            });
        }
        return op;
    });
    return {
        ops: rewritten
    };
}
